/**
 * Shared B2B/B2C session orchestration for cookie-backed sessions.
 * Platform token-based flows still use the legacy in-memory token path.
 */

import api from '../apiClient';
import {
  registerOnRefreshSuccess,
  registerPreRequestGuard,
  registerUnauthorizedRecovery,
} from '../apiClient';
import tokenManager from '../auth/tokenManager';
import { performLogout } from '../../utils/logout';
import { stripStatePrefix } from '../../utils/tenant';

const parseNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseTimestamp = (value) => {
  if (!value) return null;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : null;
};

class SessionManager {
  constructor() {
    this.ENABLED = import.meta.env.VITE_ENABLE_SESSION_ORCHESTRATION !== 'false';
    this.REFRESH_BASE_INTERVAL_MS =
      parseNumber(import.meta.env.VITE_SESSION_REFRESH_BASE_INTERVAL_SECONDS, 480) * 1000;
    this.REFRESH_JITTER_MS =
      parseNumber(import.meta.env.VITE_SESSION_REFRESH_JITTER_SECONDS, 30) * 1000;
    this.LEASE_MS =
      parseNumber(import.meta.env.VITE_SESSION_LEADER_LEASE_MS, 15000);
    this.LEASE_HEARTBEAT_MS =
      parseNumber(import.meta.env.VITE_SESSION_LEADER_HEARTBEAT_MS, 5000);
    this.WARNING_WINDOW_MS = 60 * 1000;

    // Namespace all storage/broadcast keys by tenant subdomain so sessions
    // on different tenant subdomains (e.g. tenantA.local.test vs tenantB.local.test)
    // don't leak into each other via shared localStorage or BroadcastChannel.
    const tenantSlug = (window.location.hostname.split('.')[0] || 'default').toLowerCase();
    this.CHANNEL_NAME = `mira_session_sync_${tenantSlug}`;
    this.LEADER_KEY = `mira_session_refresh_leader_${tenantSlug}`;
    this.SESSION_META_KEY = `mira_session_metadata_${tenantSlug}`;
    this.STORAGE_EVENT_KEY = `mira_session_sync_${tenantSlug}`;

    this.accessToken = null;
    this.tokenExpiry = null;
    this.refreshTimer = null;
    this.warningTimer = null;
    this.checkInterval = null;
    this.refreshPromise = null;
    this.isRefreshing = false;
    this.sessionMeta = null;
    this.lastActivity = Date.now();
    this.waiters = [];
    this.leaderHeartbeatTimer = null;
    this.initialized = false;
    this.isLeaderTab = false;

    this.tabId = this.getOrCreateTabId();
    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    this.pageHideHandler = this.handlePageHide.bind(this);
    this.storageHandler = this.handleStorageChange.bind(this);
    this.visibilityHandler = this.handleVisibilityChange.bind(this);
    this.onlineHandler = this.handleOnline.bind(this);
    this.activityHandler = this.updateActivity.bind(this);

    this.onSessionWarning = null;
    this.onSessionExpired = null;
    this.onIdleWarning = null;
    this.onRefreshSuccess = null;
    this.onRefreshFailure = null;

    this.initBroadcastChannel();
    this.initActivityTracking();
  }

  getOrCreateTabId() {
    try {
      let tabId = sessionStorage.getItem('mira_tab_id');
      if (!tabId) {
        tabId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem('mira_tab_id', tabId);
      }
      return tabId;
    } catch (error) {
      console.warn('[SessionManager] Failed to resolve tab id:', error);
      return `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
  }

  isPlatformSurface() {
    if (typeof window === 'undefined') return false;
    const path = stripStatePrefix(window.location.pathname || '/');
    return path.startsWith('/platform');
  }

  useCookieOrchestration() {
    return this.ENABLED && !this.isPlatformSurface();
  }

  currentSurface() {
    const user = tokenManager.getUserData();
    const authProvider = user?.auth_provider || user?.authProvider;
    if (authProvider === 'consumer' || String(user?.role || '').toLowerCase().includes('consumer')) {
      return 'b2c';
    }
    if (this.isPlatformSurface()) return 'platform';
    return 'b2b';
  }

  logDiagnostic(event, payload = {}) {
    const detail = {
      event,
      sessionId: this.sessionMeta?.session_id || this.sessionMeta?.id || null,
      tabId: this.tabId,
      surface: this.currentSurface(),
      isLeader: this.isLeader(),
      timestamp: new Date().toISOString(),
      ...payload,
    };

    if (!import.meta.env.PROD) {
      console.log('[SessionManager]', detail);
    }

    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('mira:session-diagnostic', { detail }));
    }
  }

  initBroadcastChannel() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(this.CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleBroadcastMessage(event.data);
    } else {
      this.channel = null;
    }
  }

  initActivityTracking() {
    if (typeof document === 'undefined') return;
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach((eventName) => {
      document.addEventListener(eventName, this.activityHandler, true);
    });
  }

  updateActivity() {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    this.lastActivity = Date.now();
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    registerPreRequestGuard(() => this.ensureTokenFresh());
    registerOnRefreshSuccess((payload) => this.handleRefreshSuccess(payload));
    registerUnauthorizedRecovery((context) => this.handleUnauthorizedRecovery(context));

    window.addEventListener('storage', this.storageHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    if (this.useCookieOrchestration()) {
      this.loadSessionMetadata();
      this.startLeaderElection();
      this.scheduleRefresh();
      this.scheduleWarnings();
      return;
    }

    this.loadTokenFromMemory();
    this.startTokenMonitoring();
  }

  handleBeforeUnload() {
    this.resignLeadership();
  }

  handlePageHide(event) {
    if (event?.persisted) {
      return;
    }
    this.resignLeadership();
  }

  handleVisibilityChange() {
    if (!this.useCookieOrchestration()) return;

    if (document.visibilityState === 'hidden') {
      // Pause refresh while tab is hidden — browser may throttle timers anyway
      this._tabHiddenAt = Date.now();
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      return;
    }

    // Tab became visible — recover session before any API calls
    this._tabHiddenAt = null;
    this.synchronizeLeadership();
    this.scheduleWarnings();
    void this.recoverSession({ reason: 'visibility_resume', allowFollowerWait: true });
  }

  handleOnline() {
    if (!this.useCookieOrchestration()) return;
    void this.recoverSession({ reason: 'network_recovery', allowFollowerWait: true });
  }

  loadSessionMetadata() {
    try {
      const raw = localStorage.getItem(this.SESSION_META_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      this.sessionMeta = parsed;
    } catch (error) {
      console.warn('[SessionManager] Failed to load session metadata:', error);
    }
  }

  persistSessionMetadata() {
    try {
      if (!this.sessionMeta) {
        localStorage.removeItem(this.SESSION_META_KEY);
        return;
      }
      localStorage.setItem(this.SESSION_META_KEY, JSON.stringify(this.sessionMeta));
    } catch (error) {
      console.warn('[SessionManager] Failed to persist session metadata:', error);
    }
  }

  normalizeSessionMetadata(metadata = {}) {
    const sessionId = metadata.session_id || metadata.id || metadata.sessionId || null;
    const normalized = {
      id: sessionId,
      session_id: sessionId,
      auth_provider: metadata.auth_provider || metadata.authProvider || this.currentSurface(),
      surface: metadata.surface || (this.currentSurface() === 'b2c' ? 'b2c' : 'b2b'),
      server_time: metadata.server_time || metadata.serverTime || null,
      idle_expires_at: metadata.idle_expires_at || metadata.idleExpiresAt || null,
      absolute_expires_at: metadata.absolute_expires_at || metadata.absoluteExpiresAt || null,
      access_expires_at: metadata.access_expires_at || metadata.accessExpiresAt || null,
      idle_timeout_seconds: metadata.idle_timeout_seconds || metadata.idleTimeoutSeconds || null,
      absolute_timeout_seconds:
        metadata.absolute_timeout_seconds || metadata.absoluteTimeoutSeconds || null,
    };
    normalized.idle_expires_at_ms = parseTimestamp(normalized.idle_expires_at);
    normalized.absolute_expires_at_ms = parseTimestamp(normalized.absolute_expires_at);
    normalized.access_expires_at_ms = parseTimestamp(normalized.access_expires_at);
    normalized.server_time_ms = parseTimestamp(normalized.server_time);
    return normalized;
  }

  hydrateSessionMetadata(metadata, source = 'unknown') {
    if (!metadata) {
      return;
    }
    this.sessionMeta = this.normalizeSessionMetadata(metadata);
    this.persistSessionMetadata();
    this.scheduleRefresh();
    this.scheduleWarnings();
    this.logDiagnostic('session_metadata_updated', { source });
  }

  scheduleWarnings() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    const nextExpiry = this.getNearestExpiry();
    if (!nextExpiry) return;

    const now = Date.now();
    if (nextExpiry <= now) {
      if (this.onSessionExpired) {
        this.onSessionExpired();
      }
      return;
    }

    const warningAt = Math.max(0, nextExpiry - now - this.WARNING_WINDOW_MS);
    this.warningTimer = setTimeout(() => {
      if (this.onSessionWarning) {
        this.onSessionWarning(Math.max(0, Math.round((nextExpiry - Date.now()) / 1000)));
      }
    }, warningAt);
  }

  getNearestExpiry() {
    const candidates = [
      this.sessionMeta?.idle_expires_at_ms,
      this.sessionMeta?.absolute_expires_at_ms,
    ].filter(Boolean);
    if (!candidates.length) return null;
    return Math.min(...candidates);
  }

  getLease() {
    try {
      const raw = localStorage.getItem(this.LEADER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('[SessionManager] Failed to parse leader lease:', error);
      return null;
    }
  }

  setLease(lease) {
    try {
      localStorage.setItem(this.LEADER_KEY, JSON.stringify(lease));
    } catch (error) {
      console.warn('[SessionManager] Failed to persist leader lease:', error);
    }
  }

  isLeaseValid(lease) {
    return Boolean(lease?.tabId && lease?.leaseExpiresAt && Date.now() < lease.leaseExpiresAt);
  }

  isLeader() {
    if (!this.useCookieOrchestration()) {
      return false;
    }
    const lease = this.getLease();
    return this.isLeaseValid(lease) && lease.tabId === this.tabId;
  }

  synchronizeLeadership() {
    const lease = this.getLease();
    this.isLeaderTab = this.isLeaseValid(lease) && lease.tabId === this.tabId;
    return this.isLeaderTab;
  }

  tryAcquireLeadership({ force = false } = {}) {
    if (!this.useCookieOrchestration()) {
      return false;
    }

    const now = Date.now();
    const current = this.getLease();
    const validCurrent = this.isLeaseValid(current);

    if (validCurrent && current.tabId !== this.tabId && !force) {
      this.isLeaderTab = false;
      return false;
    }

    const nextLease = {
      tabId: this.tabId,
      heartbeatAt: now,
      leaseExpiresAt: now + this.LEASE_MS,
      epoch: now,
    };

    this.setLease(nextLease);
    this.isLeaderTab = true;
    this.broadcast({ type: 'LEADER_UPDATED', lease: nextLease });
    this.logDiagnostic('leader_acquired', { epoch: nextLease.epoch });
    return true;
  }

  renewLeadership() {
    if (!this.useCookieOrchestration()) return;
    const current = this.getLease();
    if (!current || current.tabId !== this.tabId) {
      this.isLeaderTab = false;
      return;
    }

    const now = Date.now();
    const nextLease = {
      ...current,
      heartbeatAt: now,
      leaseExpiresAt: now + this.LEASE_MS,
      epoch: current.epoch || now,
    };
    this.setLease(nextLease);
    this.isLeaderTab = true;
  }

  resignLeadership() {
    if (!this.useCookieOrchestration()) return;
    const lease = this.getLease();
    if (lease?.tabId === this.tabId) {
      localStorage.removeItem(this.LEADER_KEY);
      this.broadcast({ type: 'LEADER_UPDATED', lease: null });
    }
    this.isLeaderTab = false;
  }

  startLeaderElection() {
    this.synchronizeLeadership();
    if (!this.isLeader()) {
      this.tryAcquireLeadership();
    }

    if (this.leaderHeartbeatTimer) {
      clearInterval(this.leaderHeartbeatTimer);
    }

    this.leaderHeartbeatTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        if (!this.synchronizeLeadership()) {
          this.tryAcquireLeadership();
        }
      }
      if (this.isLeader()) {
        this.renewLeadership();
      }
    }, this.LEASE_HEARTBEAT_MS);
  }

  getRefreshDelayMs() {
    const jitter = Math.floor(Math.random() * this.REFRESH_JITTER_MS);
    const candidates = [this.REFRESH_BASE_INTERVAL_MS + jitter];
    if (this.sessionMeta?.access_expires_at_ms) {
      candidates.push(Math.max(15_000, this.sessionMeta.access_expires_at_ms - Date.now() - 120_000));
    }
    if (this.sessionMeta?.idle_expires_at_ms) {
      candidates.push(Math.max(15_000, this.sessionMeta.idle_expires_at_ms - Date.now() - 120_000));
    }
    return Math.max(15_000, Math.min(...candidates.filter((value) => Number.isFinite(value))));
  }

  scheduleRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (!this.useCookieOrchestration()) {
      return;
    }

    const delay = this.getRefreshDelayMs();
    this.refreshTimer = setTimeout(() => {
      if (!this.isLeader()) {
        this.scheduleRefresh();
        return;
      }
      void this.refreshCookieSession({ reason: 'scheduled_refresh', allowFollowerWait: false });
    }, delay);
  }

  handleRefreshSuccess(payload) {
    const sessionPayload = payload?.session || payload?.data?.session || null;
    if (sessionPayload) {
      this.hydrateSessionMetadata(sessionPayload, 'api_refresh_success');
    } else if (this.useCookieOrchestration()) {
      this.scheduleRefresh();
    }
    if (this.onRefreshSuccess) {
      this.onRefreshSuccess(payload);
    }
  }

  classifyFailure(error, source = 'general') {
    const status = error?.response?.status || null;
    const detail = error?.response?.data?.detail ?? error?.response?.data ?? null;
    const reason = detail?.reason || detail?.error || null;
    const transientCodes = new Set([
      'ERR_NETWORK',
      'ECONNABORTED',
      'ETIMEDOUT',
    ]);

    // Only these reasons are truly terminal — no recovery possible
    const DEFINITIVE_REASONS = new Set([
      'session_revoked',
      'refresh_token_invalid',
      'tenant_mismatch',
    ]);

    // No session/token exists to refresh — retrying cannot succeed and just
    // hammers /auth/session/refresh while the user is logged out.
    const LOGGED_OUT_REASONS = new Set([
      'missing_refresh_token',
      'no_refresh_token',
      'not_authenticated',
      'no_session',
    ]);

    if (!error?.response) {
      return {
        classification: 'transient',
        reason: navigator.onLine === false ? 'offline' : 'network_failure',
        status,
        detail,
      };
    }

    if (status >= 500) {
      return { classification: 'transient', reason: 'server_error', status, detail };
    }

    if ((status === 401 || status === 403) && reason) {
      // Only truly terminal reasons trigger immediate logout
      if (DEFINITIVE_REASONS.has(reason)) {
        return { classification: 'definitive', reason, status, detail };
      }
      // No session to refresh — stop retrying (but don't force a logout).
      if (LOGGED_OUT_REASONS.has(reason)) {
        return { classification: 'inactive', reason, status, detail };
      }
      // Other 401/403 with reasons (session_not_found, session_expired, etc.)
      // are recoverable via refresh
      return { classification: 'transient', reason, status, detail };
    }

    if (status === 401 || status === 403) {
      // No reason provided — attempt recovery before giving up
      return { classification: 'transient', reason: `${source}_unauthorized`, status, detail };
    }

    if (transientCodes.has(error?.code)) {
      return { classification: 'transient', reason: error.code, status, detail };
    }

    return { classification: 'transient', reason: `${source}_degraded`, status, detail };
  }

  waitForFollowerRefresh(timeoutMs = 8000) {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        this.waiters = this.waiters.filter((entry) => entry.timeoutId !== timeoutId);
        resolve({ recovered: false, classification: 'transient', reason: 'leader_refresh_timeout' });
      }, timeoutMs);

      this.waiters.push({ resolve, timeoutId });
    });
  }

  settleWaiters(result) {
    const pending = [...this.waiters];
    this.waiters = [];
    pending.forEach(({ resolve, timeoutId }) => {
      clearTimeout(timeoutId);
      resolve(result);
    });
  }

  async refreshCookieSession({ reason = 'manual_refresh', allowFollowerWait = true } = {}) {
    if (!this.useCookieOrchestration()) {
      return { recovered: false, classification: 'unsupported', reason: 'token_mode' };
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.isLeader()) {
      const acquired = this.tryAcquireLeadership();
      if (!acquired) {
        if (allowFollowerWait) {
          return this.waitForFollowerRefresh();
        }
        return { recovered: false, classification: 'transient', reason: 'not_refresh_leader' };
      }
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      this.logDiagnostic('refresh_start', { reason });
      try {
        const response = await api.post(
          '/auth/session/refresh',
          {},
          { _skipOrchestrated401: true, _sessionRefreshReason: reason }
        );
        const payload = response?.data || {};
        this._refreshRetryCount = 0; // Reset retry counter on success
        if (payload.session) {
          this.hydrateSessionMetadata(payload.session, `refresh:${reason}`);
        }
        this.broadcast({ type: 'SESSION_REFRESH_COMPLETED', session: payload.session || null, reason });
        this.settleWaiters({ recovered: true, classification: 'success', payload });
        return { recovered: true, classification: 'success', payload };
      } catch (error) {
        const classification = this.classifyFailure(error, 'refresh');
        this.logDiagnostic('refresh_failure', { reason, failure: classification.reason, status: classification.status });
        if (this.onRefreshFailure) {
          this.onRefreshFailure(error);
        }
        // Resolved outcome returned to the direct caller. Diverges from the raw
        // classifyFailure result only for the logged-out stop-branch below, which
        // can be entered on a 401/403 the classifier still labels transient.
        let resolvedClassification = classification.classification;
        if (classification.classification === 'definitive') {
          this._refreshRetryCount = 0;
          this.broadcast({
            type: 'SESSION_INVALIDATED',
            reason: classification.reason,
            detail: classification.detail,
          });
          this.settleWaiters({
            recovered: false,
            classification: 'definitive',
            reason: classification.reason,
            error,
          });
        } else if (
          classification.classification === 'inactive' ||
          // A 401/403 with no signed-in user means logged out — stop. (Network
          // and 5xx failures still retry, so bootstrap restore isn't affected.)
          ((classification.status === 401 || classification.status === 403) && !tokenManager.getUserData())
        ) {
          // No session to refresh (logged out) — stop the retry loop instead of
          // hammering /auth/session/refresh. No invalidation broadcast; the user
          // is already signed out. A real login re-arms the scheduler.
          this._refreshRetryCount = 0;
          if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
          }
          this.logDiagnostic('refresh_stopped_no_session', { reason: classification.reason });
          resolvedClassification = 'inactive';
          // Tell follower tabs to resolve as inactive instead of sitting out the
          // full waitForFollowerRefresh timeout. Deliberately NOT SESSION_INVALIDATED
          // — the user is already signed out, so no logout cascade.
          this.broadcast({ type: 'SESSION_REFRESH_INACTIVE', reason: classification.reason });
          this.settleWaiters({
            recovered: false,
            classification: 'inactive',
            reason: classification.reason,
            error,
          });
        } else {
          // Aggressive retry with backoff instead of waiting for next scheduled refresh
          this._refreshRetryCount = (this._refreshRetryCount || 0) + 1;
          const retryDelays = [5_000, 15_000, 60_000];
          const retryDelay = retryDelays[Math.min(this._refreshRetryCount - 1, retryDelays.length - 1)];
          this.logDiagnostic('refresh_retry_scheduled', { attempt: this._refreshRetryCount, delayMs: retryDelay });

          this.broadcast({
            type: 'SESSION_REFRESH_FAILED',
            reason: classification.reason,
            detail: classification.detail,
          });
          this.settleWaiters({
            recovered: false,
            classification: 'transient',
            reason: classification.reason,
            error,
          });

          // Use retry delay instead of normal schedule
          if (this.refreshTimer) clearTimeout(this.refreshTimer);
          this.refreshTimer = setTimeout(() => {
            void this.refreshCookieSession({ reason: 'retry', allowFollowerWait: false });
          }, retryDelay);
        }
        return {
          recovered: false,
          classification: resolvedClassification,
          reason: classification.reason,
          error,
        };
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async recoverSession({ reason = 'manual_recovery', allowFollowerWait = true } = {}) {
    return this.refreshCookieSession({ reason, allowFollowerWait });
  }

  async handleUnauthorizedRecovery({ error }) {
    return this.recoverSession({ reason: 'unauthorized_response', allowFollowerWait: true, error });
  }

  async ensureTokenFresh() {
    if (this.useCookieOrchestration()) {
      return;
    }

    if (!this.tokenExpiry) return;
    const timeToExpiry = this.tokenExpiry - Date.now();
    if (timeToExpiry > 0 && timeToExpiry <= 60 * 1000) {
      await this.refreshToken();
    }
  }

  loadTokenFromMemory() {
    const token = tokenManager.getAccessToken();
    if (!token) return;
    const payload = tokenManager.parseJwt(token);
    if (payload?.exp) {
      this.accessToken = token;
      this.tokenExpiry = payload.exp * 1000;
    }
  }

  storeTokenInMemory(token) {
    const payload = tokenManager.parseJwt(token);
    if (!payload?.exp) {
      return;
    }
    this.accessToken = token;
    this.tokenExpiry = payload.exp * 1000;
    this.startTokenMonitoring();
  }

  startTokenMonitoring() {
    this.stopTokenMonitoring();
    if (!this.accessToken || !this.tokenExpiry) {
      return;
    }
    const timeToExpiry = this.tokenExpiry - Date.now();
    if (timeToExpiry > 0) {
      this.refreshTimer = setTimeout(() => {
        void this.refreshToken();
      }, Math.max(1000, timeToExpiry - 5 * 60 * 1000));
    }
  }

  stopTokenMonitoring() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  async refreshToken() {
    if (this.useCookieOrchestration()) {
      return this.refreshCookieSession({ reason: 'manual_refresh', allowFollowerWait: true });
    }

    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await api.post('/auth/session/refresh', {}, { _skipOrchestrated401: true });
        const nextToken = response?.data?.accessToken || response?.data?.access_token || null;
        if (nextToken) {
          this.storeTokenInMemory(nextToken);
        }
        return nextToken;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  handleBroadcastMessage(message = {}) {
    switch (message.type) {
      case 'SESSION_CHANGED':
        // Don't reload if this tab is waiting for a magic link to be opened elsewhere
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('mira_awaiting_magic_link')) {
          break;
        }
        window.location.reload();
        break;
      case 'SESSION_REFRESH_COMPLETED':
        if (message.session) {
          this.hydrateSessionMetadata(message.session, 'broadcast_refresh');
        } else {
          this.scheduleRefresh();
        }
        this.settleWaiters({ recovered: true, classification: 'success', payload: { session: message.session || null } });
        break;
      case 'SESSION_REFRESH_FAILED':
        this.settleWaiters({ recovered: false, classification: 'transient', reason: message.reason });
        break;
      case 'SESSION_REFRESH_INACTIVE':
        this.settleWaiters({ recovered: false, classification: 'inactive', reason: message.reason });
        break;
      case 'SESSION_INVALIDATED':
        this.settleWaiters({ recovered: false, classification: 'definitive', reason: message.reason });
        if (message.reason) {
          void this.handleDefinitiveLogout(message.reason);
        }
        break;
      case 'LEADER_UPDATED':
        this.synchronizeLeadership();
        break;
      case 'LOGOUT':
        this.handleRemoteLogout(message.reason);
        break;
      default:
        break;
    }
  }

  handleStorageChange(event) {
    if (event.key === this.STORAGE_EVENT_KEY && event.newValue) {
      try {
        this.handleBroadcastMessage(JSON.parse(event.newValue));
      } catch (error) {
        console.error('[SessionManager] Failed to parse session sync event:', error);
      }
      return;
    }

    if (event.key === this.LEADER_KEY) {
      this.synchronizeLeadership();
      return;
    }

    if (event.key === this.SESSION_META_KEY && event.newValue) {
      try {
        this.sessionMeta = JSON.parse(event.newValue);
        this.scheduleRefresh();
        this.scheduleWarnings();
      } catch (error) {
        console.error('[SessionManager] Failed to parse session metadata storage event:', error);
      }
    }
  }

  broadcast(message) {
    const payload = { ...message, tabId: this.tabId, timestamp: Date.now() };
    try {
      if (this.channel) {
        this.channel.postMessage(payload);
      } else {
        localStorage.setItem(this.STORAGE_EVENT_KEY, JSON.stringify(payload));
      }
    } catch (error) {
      localStorage.setItem(this.STORAGE_EVENT_KEY, JSON.stringify(payload));
    }
  }

  async handleDefinitiveLogout(reason = 'session_invalid') {
    this.logDiagnostic('definitive_logout', { reason });
    await performLogout({ redirect: true });
  }

  async handleRemoteLogout(reason = 'remote_logout') {
    this.logDiagnostic('remote_logout', { reason });
    await performLogout({ redirect: true });
  }

  async logout(reason = 'User initiated logout') {
    this.broadcast({ type: 'LOGOUT', reason });
    await performLogout({ redirect: true });
  }

  cleanup() {
    this.stopTokenMonitoring();
    if (this.leaderHeartbeatTimer) {
      clearInterval(this.leaderHeartbeatTimer);
      this.leaderHeartbeatTimer = null;
    }
    this.resignLeadership();

    window.removeEventListener('storage', this.storageHandler);
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    window.removeEventListener('pagehide', this.pageHideHandler);
    window.removeEventListener('online', this.onlineHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach((eventName) => {
      document.removeEventListener(eventName, this.activityHandler, true);
    });

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.waiters = [];
    this.initialized = false;
  }
}

const sessionManager = new SessionManager();
export default sessionManager;
